// @flow
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import bem from '@justpark/helpers/src/bem/bem';
import Typography from '@justpark/ui/src/components/Typography';
import DropDown from '@justpark/ui/src/components/DropDown';
import { selectIsAppFeatureLimited } from '../../stores/partner';
import Link from '../UI/Link';
import type { T } from '../../types';

import '../../../static/scss/components/policies/policyPageWrapper.scss';

type Props = {
  children: any
};

const baseClass = 'c-policy-page-wrapper';
const useGetParkDepotNavOptions = (
  t: T = (key, defaultString) => defaultString
) =>
  useMemo(
    () => [
      {
        label: t('navigation:privacyPolicy', 'Privacy Policy'),
        value: '/privacy-policy'
      },
      {
        label: t('navigation:termsAndConditions', 'Terms and Conditions'),
        value: '/terms-and-conditions'
      },
      {
        label: t(
          'navigation:reservableCancellation',
          'Cancellation - Reservable'
        ),
        value: '/cancellation-policy'
      }
    ],
    [t]
  );

const useGetDropdownOptions = (
  t: T = (key, defaultString) => defaultString,
  isWelsh: boolean
) => {
  const parkDepotNavOptions = useGetParkDepotNavOptions();
  return useMemo(
    () =>
      isWelsh
        ? [
            {
              label: t('navigation:driverAgreementMobile', 'Driver agreement'),
              value: '/driver-agreement'
            },
            {
              label: t('navigation:ownerAgreementMobile', 'Owner agreement'),
              value: '/owner-agreement'
            },
            {
              label: t('navigation:cookiePolicyMobile', 'Cookie policy'),
              value: '/cookie-policy'
            },
            ...parkDepotNavOptions,
            {
              label: t(
                'navigation:cancellationNonReservableMobile',
                'Cancellation - Non-reservable'
              ),
              value: '/cancellation-policy?section=non-reservable'
            },
            {
              label: t(
                'navigation:cancellationEVMobile',
                'Cancellation - EV Charging'
              ),
              value: '/cancellation-policy?section=ev-charging'
            },
            {
              label: t(
                'navigation:cancellationWestWittMobile',
                'Cancellation - West Wittering'
              ),
              value: '/cancellation-policy?section=west-wittering'
            },
            {
              label: t(
                'navigation:cancellationExtMobile',
                'Extenuating Circumstances'
              ),
              value: '/cancellation-policy?section=extenuating-circumstances'
            }
          ]
        : [
            {
              label: t('navigation:driverAgreementMobile', 'Driver agreement'),
              value: '/driver-agreement'
            },
            {
              label: t('navigation:ownerAgreementMobile', 'Owner agreement'),
              value: '/owner-agreement'
            },
            {
              label: t('navigation:cookiePolicyMobile', 'Cookie policy'),
              value: '/cookie-policy'
            },
            ...parkDepotNavOptions,
            {
              label: t(
                'navigation:cancellationNonReservableMobile',
                'Cancellation - Non-reservable'
              ),
              value: '/cancellation-policy?section=non-reservable'
            },
            {
              label: t(
                'navigation:cancellationEVMobile',
                'Cancellation - EV Charging'
              ),
              value: '/cancellation-policy?section=ev-charging'
            },
            {
              label: t(
                'navigation:cancellationWestWittMobile',
                'Cancellation - West Wittering'
              ),
              value: '/cancellation-policy?section=west-wittering'
            },
            {
              label: t(
                'navigation:cancellationExtMobile',
                'Extenuating Circumstances'
              ),
              value: '/cancellation-policy?section=extenuating-circumstances'
            },
            {
              label: t(
                'navigation:cancellationPPMMobile',
                'Parking Peace of Mind'
              ),
              value: '/insurance-policy'
            }
          ],
    [parkDepotNavOptions, t, isWelsh]
  );
};

const PoliciesPageWrapper = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const isWelsh = language === 'cy';

  const isAppFeatureLimited = useSelector(selectIsAppFeatureLimited);
  const dropdownOptions = useGetDropdownOptions(t, isWelsh);
  const parkDepotNavOptions = useGetParkDepotNavOptions(t);

  const { pathname, search } = location;
  const trimmedPathName = pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  const value = `${trimmedPathName}${search}`;

  return (
    <div className={baseClass}>
      <div className={bem(baseClass, 'container')}>
        <nav className={bem(baseClass, 'side-nav-container')}>
          <Typography bold className={bem(baseClass, 'menu-title')}>
            {t('navigation:policiesTitle', 'Policies')}
          </Typography>
          {!isAppFeatureLimited && (
            <Link to="/driver-agreement">
              {t('navigation:policiesAgreement', 'Agreements')}
            </Link>
          )}
          <Link to="/cancellation-policy">
            {t('navigation:cancellationPolicies', 'Cancellation Policies')}
          </Link>
          <Link to="/privacy-policy">
            {t('navigation:privacyPolicy', 'Privacy Policy')}
          </Link>
          <Link to="/cookie-policy">
            {t('navigation:cookiePolicy', 'Cookie Policy')}
          </Link>
          {!isAppFeatureLimited && !isWelsh && (
            <Link to="/insurance-policy">Parking Peace of Mind</Link>
          )}
          <Link to="/terms-and-conditions">
            {t('navigation:termsAndConditions', 'Terms and Conditions')}
          </Link>
        </nav>

        <div className={bem(baseClass, 'dropdown-nav-container')}>
          <DropDown
            onChange={navigate}
            options={
              isAppFeatureLimited ? parkDepotNavOptions : dropdownOptions
            }
            value={value}
          />
        </div>

        <div className={bem(baseClass, 'page')}>{children}</div>
      </div>
    </div>
  );
};

export default PoliciesPageWrapper;
